import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from '../../../context/SnackbarContext';
import {
  useCampaignId,
  useSpreadsheetViewType
} from '../../../context/CampaignContext';
import { useUserContextController } from '../../../context/UserContext';
import { getCrmMapping } from '../../../api/crmMapping';
import {
  getScheduledJobs,
  createScheduledJob,
  updateScheduledJob,
  deleteScheduledJob,
  type ScheduleType,
  type ScheduledJobType,
  type ScheduledJobsByTypeResponse,
  type HubSpotImportSourceConfig
} from '../../../api/campaignScheduledJobs';
import { CampaignProspectType } from '../../../api/campaigns';
import CRMImportModal from '../../topbar/CRMImportModal';
import SchedulingPanelHeader from './SchedulingPanelHeader';
import EditScheduleDialog from './EditScheduleDialog';
import ConfirmCellEnrichmentDialog from './ConfirmCellEnrichmentDialog';
import ConfirmDeleteJobDialog from './ConfirmDeleteJobDialog';
import CellEnrichmentScheduleSummary, {
  parseCellSpecsFromJobConfig
} from './CellEnrichmentScheduleSummary';
import HubSpotImportMappingSummary, {
  getHubSpotSourceConfigFromJobConfig
} from './HubSpotImportMappingSummary';
import PlanRestrictionContactModal from '../../modal/PlanRestrictionContactModal';
import { isEnterprisePlanRestrictionError } from '../../../utils/planRestrictionContactModal';

/** Schedule creation: which operations to create. */
export type ScheduleOperationType =
  | 'cell_enrichment'
  | 'crm_import'
  | 'crm_sync';

const SCHEDULE_OPERATIONS: { id: ScheduleOperationType; label: string }[] = [
  { id: 'cell_enrichment', label: 'Cell enrichment' },
  { id: 'crm_import', label: 'CRM import' },
  { id: 'crm_sync', label: 'CRM sync (export)' }
];

/** Flattened job for list display. */
export interface ScheduledJobItem {
  id: string;
  jobType: ScheduledJobType;
  scheduleType: string;
  runCount: number;
  createTs: string;
  config: Record<string, unknown>;
}

/** Scheduled jobs API returns each job with a `config` object (see getScheduledJobs response). */
function flattenJobs(data: ScheduledJobsByTypeResponse): ScheduledJobItem[] {
  const items: ScheduledJobItem[] = [];
  (data.source || []).forEach((job) => {
    items.push({
      id: job.id,
      jobType: 'campaign_prospect_ingestion',
      scheduleType: job.schedule_type || '',
      runCount: job.run_count ?? 0,
      createTs: job.create_ts || '',
      config: job.config as Record<string, unknown>
    });
  });
  (data.cell_executor || []).forEach((job) => {
    items.push({
      id: job.id,
      jobType: 'campaign_cell_execution',
      scheduleType: job.schedule_type || '',
      runCount: job.run_count ?? 0,
      createTs: job.create_ts || '',
      config: job.config as Record<string, unknown>
    });
  });
  (data.crm_sync || []).forEach((job) => {
    items.push({
      id: job.id,
      jobType: 'campaign_crm_synchronization',
      scheduleType: job.schedule_type || '',
      runCount: job.run_count ?? 0,
      createTs: job.create_ts || '',
      config: job.config as Record<string, unknown>
    });
  });
  return items;
}

function jobTypeLabel(jobType: ScheduledJobType): string {
  switch (jobType) {
    case 'campaign_prospect_ingestion':
      return 'CRM import';
    case 'campaign_cell_execution':
      return 'Cell enrichment';
    case 'campaign_crm_synchronization':
      return 'CRM sync';
    default:
      return jobType;
  }
}

const SCHEDULE_OPTIONS: { value: ScheduleType; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

/** Selection from spreadsheet for cell enrichment (row_ids 0-based). */
export type EnrichmentSelection = { row_ids: number[]; column_names: string[] };

interface SchedulingToolPanelProps {
  onClose: () => void;
  getEnrichmentSelection?: () => EnrichmentSelection | null;
}

/** Derive CRM source_type for prospect ingestion from connected accounts. Prefer HubSpot, then Salesforce. */
function getConnectedCrmSourceType(
  connectedAccounts: { app: string }[]
): string | null {
  if (connectedAccounts.some((c) => c.app === 'hubspot')) return 'hubspot';
  if (connectedAccounts.some((c) => c.app === 'salesforce_rest_api'))
    return 'salesforce';
  return null;
}

const SchedulingToolPanel: React.FC<SchedulingToolPanelProps> = ({
  onClose,
  getEnrichmentSelection
}) => {
  const campaignId = useCampaignId();
  const viewType = useSpreadsheetViewType();
  const { showSnackbar } = useSnackbar();
  const [userContext] = useUserContextController();
  const connectedAccounts = userContext?.connectedAccounts ?? [];
  const crmSourceType = getConnectedCrmSourceType(connectedAccounts);

  const [loading, setLoading] = useState(true);
  const [scheduleType, setScheduleType] = useState<ScheduleType>('daily');
  const [selectedOperation, setSelectedOperation] = useState<
    ScheduleOperationType | ''
  >('');
  const [saving, setSaving] = useState(false);

  const [jobs, setJobs] = useState<ScheduledJobItem[]>([]);
  const [viewJob, setViewJob] = useState<ScheduledJobItem | null>(null);
  const [editJob, setEditJob] = useState<ScheduledJobItem | null>(null);
  const [editScheduleType, setEditScheduleType] =
    useState<ScheduleType>('daily');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [jobPendingDelete, setJobPendingDelete] =
    useState<ScheduledJobItem | null>(null);
  /** When set, show confirmation dialog for cell enrichment with this selection. */
  const [pendingCellSelection, setPendingCellSelection] =
    useState<EnrichmentSelection | null>(null);
  /** When set, modal is closed and user can select cells; toolbar shows Confirm/Cancel to update this job's cells. */
  const [pendingCellUpdateFromToolbar, setPendingCellUpdateFromToolbar] =
    useState<{
      jobId: string;
      jobLabel: string;
      scheduleType: ScheduleType;
    } | null>(null);
  /** Snapshot for the update confirmation modal (after banner Confirm). */
  const [pendingCellUpdateSelection, setPendingCellUpdateSelection] =
    useState<EnrichmentSelection | null>(null);
  /** HubSpot import config dialog: create (no editJob) or edit (editJob set). */
  const [hubSpotImportDialogOpen, setHubSpotImportDialogOpen] = useState(false);
  const [hubSpotImportEditJob, setHubSpotImportEditJob] =
    useState<ScheduledJobItem | null>(null);
  const [enterprisePlanModalOpen, setEnterprisePlanModalOpen] = useState(false);
  const [enterprisePlanModalDetail, setEnterprisePlanModalDetail] = useState<
    string | null
  >(null);
  /** CRM export schedules require campaign CRM mapping with dedup fields (same checks as create). */
  const [crmExportMapping, setCrmExportMapping] = useState<{
    loading: boolean;
    canScheduleExport: boolean;
    reason?: 'no_mapping' | 'no_dedup' | 'error';
  }>({ loading: true, canScheduleExport: false });

  const selectedOps = {
    cell_enrichment: selectedOperation === 'cell_enrichment',
    crm_import: selectedOperation === 'crm_import',
    crm_sync: selectedOperation === 'crm_sync'
  };

  const refreshJobList = useCallback(async () => {
    if (!campaignId) {
      setJobs([]);
      return;
    }
    try {
      const data = await getScheduledJobs(campaignId);
      setJobs(flattenJobs(data));
    } catch (error: unknown) {
      showSnackbar(
        `Failed to refresh job list: ${error instanceof Error ? error.message : String(error)}`,
        'error'
      );
    }
  }, [campaignId, showSnackbar]);

  useEffect(() => {
    if (!campaignId) {
      setLoading(false);
      setJobs([]);
      return;
    }

    setLoading(true);
    getScheduledJobs(campaignId)
      .then((data) => {
        setJobs(flattenJobs(data));
      })
      .catch((error: unknown) => {
        showSnackbar(
          `Failed to load schedules: ${error instanceof Error ? error.message : String(error)}`,
          'error'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [campaignId, showSnackbar]);

  useEffect(() => {
    if (!campaignId) {
      setCrmExportMapping({ loading: false, canScheduleExport: false });
      return;
    }
    let cancelled = false;
    setCrmExportMapping({ loading: true, canScheduleExport: false });
    getCrmMapping(campaignId, viewType)
      .then(({ crm_mapping }) => {
        if (cancelled) return;
        if (!crm_mapping) {
          setCrmExportMapping({
            loading: false,
            canScheduleExport: false,
            reason: 'no_mapping'
          });
          return;
        }
        if (!crm_mapping.dedup_fields?.length) {
          setCrmExportMapping({
            loading: false,
            canScheduleExport: false,
            reason: 'no_dedup'
          });
          return;
        }
        setCrmExportMapping({ loading: false, canScheduleExport: true });
      })
      .catch(() => {
        if (cancelled) return;
        setCrmExportMapping({
          loading: false,
          canScheduleExport: false,
          reason: 'error'
        });
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, viewType]);

  const handleCreateSchedules = async () => {
    if (!campaignId) {
      showSnackbar('No campaign selected', 'error');
      return;
    }
    if (!selectedOperation) {
      showSnackbar('Select a schedule type', 'warning');
      return;
    }

    if (selectedOperation === 'cell_enrichment') {
      const selection = getEnrichmentSelection?.() ?? null;
      if (
        !selection ||
        selection.row_ids.length === 0 ||
        selection.column_names.length === 0
      ) {
        showSnackbar('Select cells in the spreadsheet first', 'warning');
        return;
      }
      setPendingCellSelection(selection);
      return;
    }

    if (
      selectedOperation === 'crm_import' ||
      selectedOperation === 'crm_sync'
    ) {
      if (!crmSourceType) {
        showSnackbar(
          'Connect a CRM (HubSpot or Salesforce) to use CRM import and export scheduling.',
          'warning'
        );
        return;
      }
    }

    if (selectedOperation === 'crm_import' && crmSourceType === 'hubspot') {
      setHubSpotImportEditJob(null);
      setHubSpotImportDialogOpen(true);
      return;
    }

    if (selectedOperation === 'crm_import' && crmSourceType === 'salesforce') {
      setSaving(true);
      try {
        await createScheduledJob(campaignId, {
          job_type: 'campaign_prospect_ingestion',
          job_config: {
            source_type: 'salesforce',
            source_config: {},
            column_mapping: []
          },
          schedule_type: scheduleType
        });
        showSnackbar('Schedule created successfully', 'success');
        setSelectedOperation('');
        await refreshJobList();
      } catch (error: unknown) {
        if (isEnterprisePlanRestrictionError(error)) {
          setEnterprisePlanModalDetail(
            error instanceof Error ? error.message : String(error)
          );
          setEnterprisePlanModalOpen(true);
        } else {
          showSnackbar(
            `Failed to create schedule: ${error instanceof Error ? error.message : String(error)}`,
            'error'
          );
        }
      } finally {
        setSaving(false);
      }
      return;
    }

    if (selectedOperation === 'crm_sync') {
      try {
        const { crm_mapping } = await getCrmMapping(campaignId, viewType);
        if (!crm_mapping) {
          showSnackbar(
            'Set up your campaign CRM mapping first, then create this schedule.',
            'warning'
          );
          return;
        }
        if (!crm_mapping.dedup_fields?.length) {
          showSnackbar(
            'Your CRM mapping must have at least one dedup (search) field.',
            'warning'
          );
          return;
        }
      } catch (error: unknown) {
        showSnackbar(
          `Could not load CRM mapping: ${error instanceof Error ? error.message : String(error)}`,
          'error'
        );
        return;
      }
      setSaving(true);
      try {
        await createScheduledJob(campaignId, {
          job_type: 'campaign_crm_synchronization',
          job_config: {},
          schedule_type: scheduleType
        });
        showSnackbar('Schedule created successfully', 'success');
        setSelectedOperation('');
        await refreshJobList();
      } catch (error: unknown) {
        if (isEnterprisePlanRestrictionError(error)) {
          setEnterprisePlanModalOpen(true);
          setEnterprisePlanModalDetail(
            error instanceof Error ? error.message : String(error)
          );
        } else {
          showSnackbar(
            `Failed to create schedule: ${error instanceof Error ? error.message : String(error)}`,
            'error'
          );
        }
      } finally {
        setSaving(false);
      }
    }
  };

  const handleHubSpotImportDialogSave = async (
    config: HubSpotImportSourceConfig
  ) => {
    if (!campaignId) return;
    setSaving(true);
    try {
      if (hubSpotImportEditJob) {
        await updateScheduledJob(campaignId, hubSpotImportEditJob.id, {
          job_config: {
            source_type: 'hubspot_import',
            source_config: config
          }
        });
        showSnackbar('Import mapping updated', 'success');
        setEditJob((prev) =>
          prev?.id === hubSpotImportEditJob.id
            ? { ...prev, config: { ...prev.config, source_config: config } }
            : prev
        );
      } else {
        await createScheduledJob(campaignId, {
          job_type: 'campaign_prospect_ingestion',
          job_config: {
            source_type: 'hubspot_import',
            source_config: config
          },
          schedule_type: scheduleType
        });
        showSnackbar('Schedule created successfully', 'success');
        setSelectedOperation('');
      }
      setHubSpotImportDialogOpen(false);
      setHubSpotImportEditJob(null);
      await refreshJobList();
    } catch (error: unknown) {
      if (isEnterprisePlanRestrictionError(error)) {
        setHubSpotImportDialogOpen(false);
        setHubSpotImportEditJob(null);
        setEnterprisePlanModalOpen(true);
        setEnterprisePlanModalDetail(
          error instanceof Error ? error.message : String(error)
        );
      } else {
        showSnackbar(
          `Failed to save: ${error instanceof Error ? error.message : String(error)}`,
          'error'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmCellEnrichment = async () => {
    if (!campaignId || !pendingCellSelection) return;
    const cells = pendingCellSelection.row_ids.flatMap((row_id) =>
      pendingCellSelection.column_names.map((column_name) => ({
        row_id,
        column_name
      }))
    );
    setSaving(true);
    try {
      await createScheduledJob(campaignId, {
        job_type: 'campaign_cell_execution',
        job_config: { cells },
        schedule_type: scheduleType
      });
      showSnackbar('Schedule created successfully', 'success');
      setPendingCellSelection(null);
      setSelectedOperation('');
      await refreshJobList();
    } catch (error: unknown) {
      if (isEnterprisePlanRestrictionError(error)) {
        setEnterprisePlanModalOpen(true);
        setEnterprisePlanModalDetail(
          error instanceof Error ? error.message : String(error)
        );
      } else {
        showSnackbar(
          `Failed to create schedule: ${error instanceof Error ? error.message : String(error)}`,
          'error'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEditOpen = (job: ScheduledJobItem) => {
    setEditJob(job);
    const st = job.scheduleType?.toLowerCase();
    setEditScheduleType(
      st === 'daily' || st === 'weekly' || st === 'monthly'
        ? (st as ScheduleType)
        : 'daily'
    );
  };

  const handleEditSave = async () => {
    if (!campaignId || !editJob) return;
    setSaving(true);
    try {
      await updateScheduledJob(campaignId, editJob.id, {
        schedule_type: editScheduleType
      });
      showSnackbar('Schedule updated', 'success');
      setEditJob(null);
      await refreshJobList();
    } catch (error: unknown) {
      if (isEnterprisePlanRestrictionError(error)) {
        setEnterprisePlanModalOpen(true);
        setEnterprisePlanModalDetail(
          error instanceof Error ? error.message : String(error)
        );
      } else {
        showSnackbar(
          `Failed to update schedule: ${error instanceof Error ? error.message : String(error)}`,
          'error'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCellsFromSelection = () => {
    if (!editJob || editJob.jobType !== 'campaign_cell_execution') return;
    setPendingCellUpdateFromToolbar({
      jobId: editJob.id,
      jobLabel: jobTypeLabel(editJob.jobType),
      scheduleType: editScheduleType
    });
    setEditJob(null);
  };

  const handleOpenCellUpdateConfirmDialog = () => {
    if (!pendingCellUpdateFromToolbar) return;
    const selection = getEnrichmentSelection?.() ?? null;
    if (
      !selection ||
      selection.row_ids.length === 0 ||
      selection.column_names.length === 0
    ) {
      showSnackbar('Select cells in the spreadsheet first', 'warning');
      return;
    }
    setPendingCellUpdateSelection(selection);
  };

  const handleApplyCellUpdateFromDialog = async () => {
    if (
      !campaignId ||
      !pendingCellUpdateFromToolbar ||
      !pendingCellUpdateSelection
    ) {
      return;
    }
    const cells = pendingCellUpdateSelection.row_ids.flatMap((row_id) =>
      pendingCellUpdateSelection.column_names.map((column_name) => ({
        row_id,
        column_name
      }))
    );
    setSaving(true);
    try {
      await updateScheduledJob(campaignId, pendingCellUpdateFromToolbar.jobId, {
        schedule_type: pendingCellUpdateFromToolbar.scheduleType,
        job_config: { cells }
      });
      showSnackbar('Schedule updated', 'success');
      setPendingCellUpdateFromToolbar(null);
      setPendingCellUpdateSelection(null);
      await refreshJobList();
    } catch (error: unknown) {
      if (isEnterprisePlanRestrictionError(error)) {
        setEnterprisePlanModalOpen(true);
        setEnterprisePlanModalDetail(
          error instanceof Error ? error.message : String(error)
        );
      } else {
        showSnackbar(
          `Failed to update schedule: ${error instanceof Error ? error.message : String(error)}`,
          'error'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (job: ScheduledJobItem) => {
    setJobPendingDelete(job);
  };

  const handleConfirmDelete = async () => {
    if (!campaignId || !jobPendingDelete) return;
    const job = jobPendingDelete;
    setDeletingId(job.id);
    try {
      await deleteScheduledJob(campaignId, job.id);
      showSnackbar('Schedule deleted', 'success');
      setViewJob((prev) => (prev?.id === job.id ? null : prev));
      setEditJob((prev) => (prev?.id === job.id ? null : prev));
      setJobPendingDelete(null);
      await refreshJobList();
    } catch (error: unknown) {
      if (isEnterprisePlanRestrictionError(error)) {
        setEnterprisePlanModalOpen(true);
        setEnterprisePlanModalDetail(
          error instanceof Error ? error.message : String(error)
        );
      } else {
        showSnackbar(
          `Failed to delete schedule: ${error instanceof Error ? error.message : String(error)}`,
          'error'
        );
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const crmSyncBlockedByMapping =
    selectedOperation === 'crm_sync' &&
    !!crmSourceType &&
    (crmExportMapping.loading || !crmExportMapping.canScheduleExport);
  const createScheduleDisabled =
    saving ||
    !selectedOperation ||
    ((selectedOperation === 'crm_import' || selectedOperation === 'crm_sync') &&
      !crmSourceType) ||
    crmSyncBlockedByMapping;
  const crmSyncMappingTooltip = crmSyncBlockedByMapping
    ? crmExportMapping.loading
      ? 'Checking CRM mapping…'
      : crmExportMapping.reason === 'no_mapping'
        ? 'Open CRM Sync in the sidebar to set up your campaign CRM mapping first.'
        : crmExportMapping.reason === 'no_dedup'
          ? 'Add at least one dedup (search) field in CRM Sync in the sidebar.'
          : crmExportMapping.reason === 'error'
            ? 'Could not verify CRM mapping. Try again.'
            : 'CRM mapping is not ready to schedule export yet.'
    : '';

  const createScheduleButton = (
    <Button
      variant="contained"
      fullWidth
      onClick={handleCreateSchedules}
      disabled={createScheduleDisabled}
      sx={(theme) => {
        const primary =
          theme.customColors?.themePrimary || theme.palette.primary.main;
        const secondary = theme.customColors?.themeSecondary || 'white';
        return {
          borderRadius: '50px',
          textTransform: 'capitalize',
          py: 1.5,
          backgroundColor: primary,
          color: secondary,
          fontWeight: 600,
          fontSize: '0.95rem',
          '&:hover': {
            backgroundColor: primary,
            opacity: 0.9,
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 20px ${primary}40`
          },
          transition: 'all 0.2s ease'
        };
      }}
    >
      {saving ? 'Creating...' : 'Create schedule'}
    </Button>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SchedulingPanelHeader onClose={onClose} />

      {/* After "Update cells" in edit dialog: select new range, then confirm (shown at top so it stays visible). */}
      {pendingCellUpdateFromToolbar && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'primary.main',
            bgcolor: 'action.hover'
          }}
        >
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Update enrichment cells
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Select the cells in the spreadsheet for{' '}
            <strong>{pendingCellUpdateFromToolbar.jobLabel}</strong>, then click
            Confirm to review and save.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setPendingCellUpdateFromToolbar(null);
                setPendingCellUpdateSelection(null);
              }}
              sx={(theme) => ({
                border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
                color:
                  theme.customColors?.themePrimary ||
                  theme.palette.primary.main,
                borderRadius: '50px',
                textTransform: 'capitalize'
              })}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleOpenCellUpdateConfirmDialog}
              disabled={saving}
              sx={(theme) => ({
                backgroundColor:
                  theme.customColors?.themePrimary ||
                  theme.palette.primary.main,
                color: theme.customColors?.themeSecondary || 'white',
                borderRadius: '50px',
                textTransform: 'capitalize'
              })}
            >
              {saving ? 'Updating...' : 'Confirm'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Create new schedules */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Create new schedules
      </Typography>
      <FormControl fullWidth size="small">
        <InputLabel id="schedule-ops-label">Schedule type</InputLabel>
        <Select
          labelId="schedule-ops-label"
          value={selectedOperation}
          label="Schedule type"
          onChange={(e) =>
            setSelectedOperation(e.target.value as ScheduleOperationType | '')
          }
        >
          <MenuItem value="">
            <em>Select schedule type</em>
          </MenuItem>
          {SCHEDULE_OPERATIONS.map(({ id, label }) => (
            <MenuItem key={id} value={id}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Type-specific options */}
      {selectedOps.cell_enrichment && (
        <Box
          sx={{
            py: 1,
            px: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
            borderLeft: '3px solid',
            borderColor: 'primary.main'
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            Cell enrichment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select the cells in the spreadsheet that you want to schedule for
            enrichment. Then click &quot;Create schedule&quot; to confirm.
          </Typography>
        </Box>
      )}
      {selectedOps.crm_import && (
        <Box
          sx={{
            py: 1,
            px: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
            borderLeft: '3px solid',
            borderColor: 'primary.main'
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            CRM import
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {crmSourceType
              ? `Imports from your connected ${crmSourceType === 'hubspot' ? 'HubSpot' : 'Salesforce'} on the chosen schedule.`
              : 'Connect a CRM (HubSpot or Salesforce) in Settings to use CRM import scheduling.'}
          </Typography>
        </Box>
      )}
      {selectedOps.crm_sync && (
        <Box
          sx={{
            py: 1,
            px: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
            borderLeft: '3px solid',
            borderColor: 'primary.main'
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            CRM sync (export)
          </Typography>
          {!crmSourceType ? (
            <Typography variant="body2" color="text.secondary">
              Connect a CRM (HubSpot or Salesforce) in Settings to use CRM
              export scheduling.
            </Typography>
          ) : crmExportMapping.loading ? (
            <Typography variant="body2" color="text.secondary">
              Checking your CRM mapping…
            </Typography>
          ) : !crmExportMapping.canScheduleExport ? (
            <Typography variant="body2" color="text.secondary">
              {crmExportMapping.reason === 'no_mapping' &&
                'No CRM mapping for this campaign yet. Open CRM Sync in the sidebar, configure field and dedup mapping, then return here to create this schedule.'}
              {crmExportMapping.reason === 'no_dedup' &&
                'Your CRM mapping must include at least one dedup (search) field. Open CRM Sync in the sidebar to edit the mapping, then try again.'}
              {crmExportMapping.reason === 'error' &&
                'Could not load CRM mapping. Check your connection or open CRM Sync in the sidebar to verify your setup.'}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {`Exports campaign data to your connected ${crmSourceType === 'hubspot' ? 'HubSpot' : 'Salesforce'} on the chosen schedule (uses your campaign CRM mapping).`}
            </Typography>
          )}
        </Box>
      )}

      <FormControl fullWidth size="small">
        <InputLabel id="schedule-type-label">Schedule frequency</InputLabel>
        <Select
          labelId="schedule-type-label"
          value={scheduleType}
          label="Schedule frequency"
          onChange={(e) => setScheduleType(e.target.value as ScheduleType)}
        >
          {SCHEDULE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {crmSyncMappingTooltip ? (
        <Tooltip title={crmSyncMappingTooltip} placement="top">
          <span style={{ display: 'block', width: '100%' }}>
            {createScheduleButton}
          </span>
        </Tooltip>
      ) : (
        createScheduleButton
      )}

      <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
        Scheduled jobs
      </Typography>

      {jobs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No scheduled jobs. Create one above.
        </Typography>
      ) : (
        <List dense disablePadding>
          {jobs.map((job) => (
            <ListItem
              key={job.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 0.5,
                py: 0.5
              }}
            >
              <ListItemText
                primary={jobTypeLabel(job.jobType)}
                secondary={`${job.scheduleType} · ${job.runCount} runs`}
                primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  size="small"
                  aria-label="View"
                  onClick={() => setViewJob(job)}
                  sx={(theme) => ({
                    color:
                      theme.customColors?.themeNeutralSecondaryColor ??
                      theme.palette.text.secondary,
                    '&:hover': {
                      color:
                        theme.customColors?.themePrimary ??
                        theme.palette.primary.main
                    }
                  })}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Edit"
                  onClick={() => handleEditOpen(job)}
                  sx={(theme) => ({
                    color:
                      theme.customColors?.themeNeutralSecondaryColor ??
                      theme.palette.text.secondary,
                    '&:hover': {
                      color:
                        theme.customColors?.themePrimary ??
                        theme.palette.primary.main
                    }
                  })}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Delete"
                  onClick={() => handleDelete(job)}
                  disabled={deletingId === job.id}
                  sx={(theme) => ({
                    color:
                      theme.customColors?.themeNeutralSecondaryColor ??
                      theme.palette.text.secondary,
                    '&:hover': {
                      color:
                        theme.customColors?.themeDanger ??
                        theme.palette.error.main
                    }
                  })}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Dialog
        open={!!viewJob}
        onClose={() => setViewJob(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {viewJob ? jobTypeLabel(viewJob.jobType) : ''}
        </DialogTitle>
        <DialogContent>
          {viewJob &&
            (() => {
              const job = viewJob;
              const hubSpotSource = getHubSpotSourceConfigFromJobConfig(
                job.config as Record<string, unknown>
              );
              return (
                <Box sx={{ pt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Schedule: {job.scheduleType}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Run count: {job.runCount}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Created:{' '}
                    {job.createTs
                      ? new Date(job.createTs).toLocaleString()
                      : '—'}
                  </Typography>
                  {job.jobType === 'campaign_cell_execution' ? (
                    <Box sx={{ mt: 2 }}>
                      <CellEnrichmentScheduleSummary
                        scheduleTypeLabel={job.scheduleType || ''}
                        cellSpecs={parseCellSpecsFromJobConfig(job.config)}
                      />
                    </Box>
                  ) : hubSpotSource ? (
                    <HubSpotImportMappingSummary sourceConfig={hubSpotSource} />
                  ) : (
                    Object.keys(job.config).length > 0 && (
                      <>
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                          Config:
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            mt: 0.5,
                            p: 1,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            overflow: 'auto',
                            maxHeight: 200
                          }}
                        >
                          {JSON.stringify(job.config, null, 2)}
                        </Box>
                      </>
                    )
                  )}
                </Box>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewJob(null)}
            sx={(theme) => ({
              border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
              backgroundColor: theme.customColors?.themeSecondary,
              color:
                theme.customColors?.themePrimary || theme.palette.primary.main,
              borderRadius: '50px',
              textTransform: 'capitalize',
              '&:hover': {
                border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
                backgroundColor:
                  theme.customColors?.themePrimary ||
                  theme.palette.primary.main,
                color: theme.customColors?.themeSecondary || 'white'
              }
            })}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (viewJob) {
                setViewJob(null);
                handleEditOpen(viewJob);
              }
            }}
            sx={(theme) => ({
              border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
              backgroundColor:
                theme.customColors?.themePrimary || theme.palette.primary.main,
              color: theme.customColors?.themeSecondary || 'white',
              borderRadius: '50px',
              textTransform: 'capitalize',
              '&:hover': {
                backgroundColor:
                  theme.customColors?.themePrimary ||
                  theme.palette.primary.main,
                opacity: 0.9
              }
            })}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      <EditScheduleDialog
        open={!!editJob}
        job={editJob}
        scheduleType={editScheduleType}
        onScheduleTypeChange={(next) => setEditScheduleType(next)}
        onClose={() => setEditJob(null)}
        onSave={handleEditSave}
        saving={saving}
        onUpdateCellsFromSelection={handleUpdateCellsFromSelection}
        onEditHubSpotImportMapping={(job) => {
          setHubSpotImportEditJob(job);
          setHubSpotImportDialogOpen(true);
        }}
        scheduleOptions={SCHEDULE_OPTIONS}
      />

      <ConfirmCellEnrichmentDialog
        open={!!pendingCellSelection}
        selection={pendingCellSelection}
        scheduleLabel={scheduleType}
        saving={saving}
        onClose={() => setPendingCellSelection(null)}
        onConfirm={handleConfirmCellEnrichment}
        variant="create"
      />

      <ConfirmCellEnrichmentDialog
        open={!!pendingCellUpdateFromToolbar && !!pendingCellUpdateSelection}
        selection={pendingCellUpdateSelection}
        scheduleLabel={pendingCellUpdateFromToolbar?.scheduleType ?? ''}
        saving={saving}
        onClose={() => setPendingCellUpdateSelection(null)}
        onConfirm={handleApplyCellUpdateFromDialog}
        variant="update"
      />

      <ConfirmDeleteJobDialog
        open={!!jobPendingDelete}
        job={jobPendingDelete}
        deleting={!!deletingId}
        onClose={() => setJobPendingDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <CRMImportModal
        open={hubSpotImportDialogOpen}
        onClose={() => {
          setHubSpotImportDialogOpen(false);
          setHubSpotImportEditJob(null);
        }}
        prospectType={CampaignProspectType.ACCOUNT}
        scheduleConfig={{
          mode: 'hubspot_import',
          initialConfig: hubSpotImportEditJob
            ? (
                hubSpotImportEditJob.config as {
                  source_config: HubSpotImportSourceConfig;
                }
              ).source_config
            : null,
          onScheduleConfirm: handleHubSpotImportDialogSave
        }}
      />

      <PlanRestrictionContactModal
        open={!!enterprisePlanModalOpen}
        onClose={() => {
          setEnterprisePlanModalOpen(false);
          setEnterprisePlanModalDetail(null);
        }}
        detail={enterprisePlanModalDetail}
      />
    </Box>
  );
};

export default SchedulingToolPanel;
export type {
  ScheduleFrequency,
  DataSourceType,
  DataImportSchedule
} from './types';
